import React from "react";
import styled from "styled-components";
import numeral from "numeral";
import moment from "moment";
import { ButtonText } from "@loanbossinc/component-library";
import { Checkbox, FormControl, FormControlLabel, Typography } from "@material-ui/core";
import _ from "lodash";

const SectionWrapper = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #dadce0;
  box-sizing: border-box;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
  border-radius: 8px;
  margin-bottom: 43px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  font-family: 'Work Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dadce0;
  padding: 0.7em 0 0.7em 1em;
`;

const StyledButtonText = styled(ButtonText)`
  margin-right: 1em !important;
  .label {
    margin-right: 14px;
    font-family: "Work Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
  }
`;

const CalculationComponentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.7em 1em;
  border-top: ${({withBorder}) => withBorder && "1px solid #dadce0"};
`;

const LeftSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RightSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
`;

const AsOfDateWrapper = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: #5f6368;
`;

const ValueWrapper = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  margin-bottom: 12px;
`;

const IncludeWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledCheckbox = styled(Checkbox)`
  height: 25px !important;
  & .Mui-checked {
    color: #2c98f0 !important;
  }
`;


const ReportCalculationsSection = ({taskDetails, updateInstanceDetails, history}) => {
  const {reportComponentsConfig, reportGroup, reportGroupInstance, deal } = taskDetails;

  const rgi = reportGroupInstance.Items[0];

  const dscrAndDyComponents = _.orderBy(reportComponentsConfig.filter((component) => component.type !== "NOI"), ["type"], ["asc"]);

  const calculatedDscr = rgi.reportInstances && rgi.reportInstances.find((reportInstance) => reportInstance.type === "DSCR");
  const calculatedDy = rgi.reportInstances && rgi.reportInstances.find((reportInstance) => reportInstance.type === "DebtYield");

  const getIncludeCheckboxState = (type) => {
    let value;
    // if shouldInclude is undefined, we default to include it in the report
    if (type === "DSCR") {
      const shouldIncludeDscr = rgi.shouldIncludeDscrInReport;
      value = shouldIncludeDscr === false ? false : true;
    } else {
      const shouldIncludeDebtYield = rgi.shouldIncludeDebtYieldInReport;
      value = shouldIncludeDebtYield === false ? false : true;
    }
    return value;
  };

  const getCalculatedValue = (reportInstanceType) => {
    if (reportInstanceType === "DSCR" && calculatedDscr && calculatedDscr.total) {
      return `${numeral(calculatedDscr.total).format("0.00", Math.round)}x`;
    } else if (reportInstanceType === "DebtYield" && calculatedDy && calculatedDy.total) {
      return numeral(calculatedDy.total).format("0.00%", Math.round);
    } else {
      return "--";
    }
  };

  const getFormattedAsOfDate = () => {
    if (!rgi.asOfDate) {
      return "As of --";
    }

    return `As of ${moment.utc(rgi.asOfDate).format("MMMM D, YYYY")}`;
  };

  const getFormattedType = (type) => {
    if (type === "DSCR") {
      return "DSCR";
    }
    return "Debt Yield";
  };


  const goTo = () => {
    history.push(`/deal/${deal.id}/lender-reports/${reportGroup.id}`);
  };

  const handleIncludeChange = (type) => {
    const propToUpdate = type === "DSCR" ? "shouldIncludeDscrInReport" : "shouldIncludeDebtYieldInReport";
    const updatedRgi = {
      ...rgi,
      [propToUpdate]: !getIncludeCheckboxState(type)
    };

    updateInstanceDetails({reportId: updatedRgi.id, reportGroupInstance: updatedRgi});
  };

  return (
    <SectionWrapper>
      <HeaderRow>
        <div>Report Calculations</div>
        <StyledButtonText label="See details" iconName="launch" alignRight={false} onClick={goTo} />
      </HeaderRow>
      {dscrAndDyComponents.map((component, index) => {
        return (
          <CalculationComponentWrapper key={component.type} withBorder={index !== 0}>
            <LeftSectionWrapper>
              <div style={{ marginBottom: "12px" }}>{getFormattedType(component.type)}</div>
              <AsOfDateWrapper>{getFormattedAsOfDate()}</AsOfDateWrapper>
            </LeftSectionWrapper>
            <RightSectionWrapper>
              <ValueWrapper>{getCalculatedValue(component.type)}</ValueWrapper>
              <IncludeWrapper>
                <FormControl>
                  <FormControlLabel
                    control={<StyledCheckbox name="include-checkbox" />}
                    id="include-status"
                    label={<Typography style={{ fontSize: 16, fontFamily: "Work Sans" }}>Include in report</Typography>}
                    labelPlacement={"start"}
                    style={{ marginRight: "-8px" }}
                    checked={getIncludeCheckboxState(component.type)}
                    onChange={() => handleIncludeChange(component.type)}
                  />
                </FormControl>
              </IncludeWrapper>
            </RightSectionWrapper>
          </CalculationComponentWrapper>
        );
      })}
    </SectionWrapper>
  );
};

export default ReportCalculationsSection;
